import { useEffect } from 'react';

import { useAccessToken } from '@mint-lib/auth';
import { useProfile } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';
import { onApprovalStatusUpdated } from '@mint-sdk/api-approval-flows/socket';

import {
  acquireApprovalSocketConnection,
  joinEntityRoom,
  leaveEntityRoom,
  releaseApprovalSocketConnection,
} from '../socket/connectionManager.js';
import type { EntitySpecificFields } from '../types/shared.js';

export type EntityRef = Pick<
  EntitySpecificFields,
  'entityId' | 'entityType' | 'entitySource'
>;

const useApprovalSocketConnection = (entities: EntityRef[]): void => {
  const accessToken = useAccessToken();
  const { getProfileField } = useProfile();
  const workspaceId = getProfileField('workspace')?.id;
  const companyId = workspaceId !== undefined ? String(workspaceId) : undefined;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    acquireApprovalSocketConnection(accessToken);

    return () => {
      releaseApprovalSocketConnection();
    };
  }, [accessToken]);

  const entityKey = entities
    .map(
      (entity) =>
        `${entity.entityType}:${entity.entityId}:${entity.entitySource}`,
    )
    .sort((a: string, b: string) => a.localeCompare(b))
    .join('|');

  useEffect(() => {
    if (!companyId || entities.length === 0) {
      return;
    }

    const params = entities.map((entity) => ({
      companyId,
      approvalItemId: entity.entityId,
      approvalItemType: entity.entityType,
    }));

    params.forEach(joinEntityRoom);

    return () => {
      params.forEach(leaveEntityRoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on entityKey, not `entities`
  }, [companyId, entityKey]);

  useEffect(() => {
    return onApprovalStatusUpdated((payload) => {
      ApprovalFlows.checkFlowStatus.invalidate(queryClient, {
        params: {
          entityId: payload.entityId,
          entityType: payload.entityType,
          entitySource: payload.entitySource,
        },
      });

      if (payload.requestId) {
        ApprovalFlows.getRequest.invalidate(queryClient, {
          urlParams: { requestId: payload.requestId },
        });
      }
    });
  }, [queryClient]);
};

export default useApprovalSocketConnection;
